import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface Slide {
  id: number;
  image: string;
  badge: string;
  headline: string;
  highlight: string;
  subtext: string;
  cta1: { label: string; path: string };
  cta2: { label: string; path: string };
  compact?: boolean;
}

const slides: Slide[] = [
  {
    id: 1,
    image: 'https://readdy.ai/api/search-image?query=modern%20large%20scale%20warehouse%20interior%20with%20high%20ceilings%20organized%20shelving%20forklifts%20in%20operation%20dramatic%20lighting%20industrial%20logistics%20facility%20Nigeria%20Africa%20dark%20moody%20tones%20professional%20commercial%20photography%20wide%20angle&width=1920&height=1080&seq=hero1&orientation=landscape',
    badge: "Nigeria's Leading Fulfilment Platform",
    headline: 'World-Class Logistics\nfor Every Business Size',
    highlight: 'Every Business Size',
    subtext:
      'Ambitious businesses across Nigeria were stalling, not from lack of vision, but from weak logistics foundations. The Hub Terminus Limited was founded to change that.',
    cta1: { label: 'Request a Quote →', path: '/contact' },
    cta2: { label: 'Book a Facility Tour →', path: '/facilities' },
  },
  {
    id: 2,
    image: 'https://readdy.ai/api/search-image?query=expansive%20modern%20fulfillment%20center%20with%20automated%20conveyor%20belts%20rows%20of%20stacked%20pallets%20workers%20in%20high%20visibility%20vests%20clean%20bright%20industrial%20space%20aerial%20perspective%20Africa%20logistics&width=1920&height=1080&seq=hero2&orientation=landscape',
    badge: 'Flexible, Pay-As-You-Use Storage',
    headline: 'Storage Built\nAround Your Business',
    highlight: 'Around Your Business',
    subtext:
      'No long-term leases. No idle space. Access enterprise-grade warehousing on your terms and scale up or down as your business demands.',
    cta1: { label: 'View Our Facilities →', path: '/facilities' },
    cta2: { label: 'Talk to Us →', path: '/contact' },
  },
  {
    id: 3,
    image: 'https://readdy.ai/api/search-image?query=vibrant%20African%20retail%20ecommerce%20operations%20young%20entrepreneur%20packaging%20products%20for%20online%20orders%20modern%20bright%20workspace%20laptops%20mobile%20devices%20Nigeria%20commerce%20growth%20digital%20economy%20wide%20cinematic%20shot%20warm%20tones&width=1920&height=1080&seq=hero3c&orientation=landscape',
    badge: 'End-to-End Fulfillment Ecosystem',
    headline: 'Fulfillment That\nLevels the Playing Field',
    highlight: 'Levels the Playing Field',
    subtext:
      'From outsourced and in-house smart fulfillment, to kitting, global distribution, and advisory support: one ecosystem built to simplify your entire supply chain.',
    cta1: { label: 'Explore Our Services →', path: '/services' },
    cta2: { label: 'Request a Quote →', path: '/contact' },
  },
  {
    id: 4,
    image: 'https://readdy.ai/api/search-image?query=wide%20open%20floor%20warehouse%20interior%20showing%20busy%20operations%20workers%20in%20high%20visibility%20vests%20operating%20forklifts%20moving%20pallets%20tall%20shelving%20racks%20stacked%20with%20goods%20bright%20overhead%20industrial%20lighting%20clean%20spacious%20facility%20Africa%20Nigeria%20active%20fulfillment%20center%20ground%20level%20wide%20angle%20cinematic%20vivid&width=1920&height=1080&seq=hero4e&orientation=landscape',
    badge: 'Proprietary Order Management System',
    headline: 'Real-Time Visibility\nAcross Every Stage',
    highlight: 'Every Stage',
    subtext:
      'Our proprietary OMS gives you real-time visibility, control, and data-driven decision-making across every stage of your supply chain, from inbound to last-mile.',
    cta1: { label: 'See Our Technology →', path: '/technology' },
    cta2: { label: 'Talk to Us →', path: '/contact' },
    compact: true,
  },
  {
    id: 5,
    image: 'https://readdy.ai/api/search-image?query=smiling%20African%20warehouse%20professional%20in%20smart%20uniform%20holding%20a%20digital%20tablet%20showing%20live%20inventory%20dashboard%20with%20colorful%20graphs%20and%20stock%20levels%20bright%20modern%20warehouse%20shelves%20full%20of%20neatly%20organized%20products%20in%20background%20clean%20crisp%20professional%20photography%20vibrant%20lighting%20sharp%20focus&width=1920&height=1080&seq=hero5c&orientation=landscape',
    badge: 'A Logistics Partner Invested in Your Growth',
    headline: 'More Than Logistics.\nA Partner in Growth',
    highlight: 'A Partner in Growth',
    subtext:
      'Serving retail brands, e-commerce businesses, pharma companies, renewable energy firms, and MSMEs, all united by one need: a partner as invested in their growth as they are.',
    cta1: { label: 'Who We Serve →', path: '/industries' },
    cta2: { label: 'Get Started →', path: '/contact' },
    compact: true,
  },
  {
    id: 6,
    image: 'https://readdy.ai/api/search-image?query=joyful%20African%20woman%20at%20her%20front%20door%20smiling%20brightly%20while%20receiving%20a%20neatly%20wrapped%20package%20from%20a%20cheerful%20delivery%20person%20in%20professional%20uniform%20sunny%20bright%20day%20colorful%20modern%20neighbourhood%20Nigeria%20vibrant%20warm%20light%20wide%20shot%20no%20vehicle%20no%20van%20no%20inscriptions%20focus%20on%20human%20connection%20and%20happiness&width=1920&height=1080&seq=hero6b&orientation=landscape',
    badge: 'Reliable. Tailored. Delivered.',
    headline: 'Solutions Tailored\nto Each Client\'s Needs',
    highlight: "Each Client's Needs",
    subtext:
      'Whether managing seasonal demand fluctuations, supporting market expansion, or building efficient operational systems, we deliver solutions that are reliable and bespoke.',
    cta1: { label: 'Explore Fulfillment →', path: '/services' },
    cta2: { label: 'Book a Tour →', path: '/facilities' },
    compact: true,
  },
];

const stats = [
  { value: '100K+', label: 'Sq Ft Storage' },
  { value: 'Global', label: 'Clients Served' },
  { value: '99.7%', label: 'Order Accuracy' },
  { value: '24hr', label: 'Dispatch SLA' },
];

export default function HeroSection() {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  const goTo = useCallback(
    (index: number) => {
      if (animating || index === current) return;
      setAnimating(true);
      setCurrent(index);
      setTimeout(() => setAnimating(false), 800);
    },
    [animating, current]
  );

  const goNext = useCallback(() => {
    goTo((current + 1) % slides.length);
  }, [current, goTo]);

  const goPrev = useCallback(() => {
    goTo((current - 1 + slides.length) % slides.length);
  }, [current, goTo]);

  useEffect(() => {
    const timer = setInterval(goNext, 6000);
    return () => clearInterval(timer);
  }, [goNext]);

  const slide = slides[current];

  const renderHeadline = (headline: string, highlight: string) => {
    const parts = headline.split(highlight);
    if (parts.length === 1) {
      return headline.split('\n').map((line, li) => (
        <span key={li} className="block">{line}</span>
      ));
    }
    const full = headline.replace('\n', '\u000A');
    const lines = full.split('\n');
    return lines.map((line, li) => {
      const idx = line.indexOf(highlight);
      if (idx !== -1) {
        return (
          <span key={li} className="block">
            {line.slice(0, idx)}
            <span className="text-thl-red">{highlight}</span>
            {line.slice(idx + highlight.length)}
          </span>
        );
      }
      return <span key={li} className="block">{line}</span>;
    });
  };

  const headlineClass =
    'text-5xl md:text-6xl lg:text-[5rem] font-extrabold text-white leading-tight mb-5 max-w-3xl hero-slide-up';

  return (
    <section className="relative w-full h-screen min-h-[640px] overflow-hidden">
      {/* Slides */}
      {slides.map((s, i) => (
        <div
          key={s.id}
          className="absolute inset-0 transition-opacity duration-[800ms] ease-in-out"
          style={{ opacity: i === current ? 1 : 0, zIndex: i === current ? 2 : 1 }}
        >
          <img
            src={s.image}
            alt={s.badge}
            className="absolute inset-0 w-full h-full object-cover object-center"
            draggable={false}
          />
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.60) 0%, rgba(0,0,0,0.30) 45%, rgba(0,0,0,0.80) 100%)' }}
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ backgroundImage: 'radial-gradient(ellipse at 20% 50%, rgba(232,25,44,0.16) 0%, transparent 55%)' }}
          />
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col justify-center">
        <div className="max-w-7xl mx-auto px-8 w-full pt-24 pb-16">

          {/* Badge */}
          <div key={`badge-${current}`} className="inline-flex items-center gap-2 mb-5 hero-slide-up">
            <span className="w-1.5 h-1.5 rounded-full bg-thl-red animate-pulse flex-shrink-0" />
            <span className="text-xs font-semibold text-white/70 uppercase tracking-widest whitespace-nowrap">
              {slide.badge}
            </span>
          </div>

          {/* Headline */}
          <h1
            key={`h-${current}`}
            className={headlineClass}
            style={{ animationDelay: '80ms' }}
          >
            {renderHeadline(slide.headline, slide.highlight)}
          </h1>

          {/* Subtext */}
          <p
            key={`p-${current}`}
            className="text-base md:text-lg text-white/65 max-w-lg leading-relaxed mb-8 hero-slide-up"
            style={{ animationDelay: '160ms' }}
          >
            {slide.subtext}
          </p>

          {/* CTAs */}
          <div
            key={`cta-${current}`}
            className="flex flex-wrap items-center gap-4 mb-12 hero-slide-up"
            style={{ animationDelay: '240ms' }}
          >
            <button
              onClick={() => navigate(slide.cta1.path)}
              className="btn-primary whitespace-nowrap cursor-pointer"
            >
              {slide.cta1.label}
            </button>
            <button
              onClick={() => navigate(slide.cta2.path)}
              className="btn-ghost whitespace-nowrap cursor-pointer"
            >
              {slide.cta2.label}
            </button>
          </div>

          {/* Stats bar */}
          <div
            key={`stats-${current}`}
            className="flex flex-wrap items-center gap-8 hero-slide-up"
            style={{ animationDelay: '320ms' }}
          >
            {stats.map((stat, si) => (
              <div key={stat.label} className="flex items-center gap-6">
                <div>
                  <div className="text-2xl font-extrabold text-white leading-none">{stat.value}</div>
                  <div className="text-xs text-white/45 uppercase tracking-wide mt-1">{stat.label}</div>
                </div>
                {si < stats.length - 1 && (
                  <div className="w-px h-8 bg-white/15 hidden sm:block" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Prev / Next arrows */}
      <button
        onClick={goPrev}
        className="absolute left-5 top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center rounded-full border border-white/20 bg-black/30 text-white/70 hover:bg-thl-red hover:border-thl-red hover:text-white transition-all duration-200 cursor-pointer"
        aria-label="Previous slide"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        onClick={goNext}
        className="absolute right-5 top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center rounded-full border border-white/20 bg-black/30 text-white/70 hover:bg-thl-red hover:border-thl-red hover:text-white transition-all duration-200 cursor-pointer"
        aria-label="Next slide"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>

      {/* Dot indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3">
        {slides.map((s, i) => (
          <button
            key={s.id}
            onClick={() => goTo(i)}
            className="cursor-pointer transition-all duration-300"
            aria-label={`Go to slide ${i + 1}`}
          >
            <span
              className={`block h-1 rounded-full transition-all duration-500 ${
                i === current ? 'w-8 bg-thl-red' : 'w-3 bg-white/30 hover:bg-white/60'
              }`}
            />
          </button>
        ))}
      </div>

      {/* Slide counter */}
      <div className="absolute bottom-8 right-6 z-20 text-white/30 text-xs font-mono tracking-widest">
        {String(current + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
      </div>
    </section>
  );
}
